import React, { memo, useMemo } from 'react';
import { ordinalSuffixOf } from '../utils';
import { Container } from './MonthModifiersUI';
import { dayOfWeek, weekDays } from '../WeekDays/WeekDays';
import { capitalize } from 'lodash';
import { DayOption } from './utils';
import { SingleSelectMenu } from '../../Dropdown';

interface IProps {
  rrule: any;
  onSelectMonthModifier: any;
}

enum MonthOptionTypeEnum {
  Interval = 'interval',
  Frequency = 'frequency',
}

export default memo(function MonthModifiers({
  rrule,
  onSelectMonthModifier,
}: IProps) {
  const currentInterval = useMemo(
    () => rrule?.origOptions?.bymonthday || rrule?.origOptions?.byweekday?.n,
    [rrule],
  );

  const currentFrequency = useMemo(() => {
    if (rrule?.origOptions?.byweekday) {
      // get the index of the weekday from weekDays array.
      const index = weekDays.findIndex(
        ({ weekday }: any) => rrule.origOptions.byweekday.weekday === weekday,
      );
      if (index > -1) {
        return index;
      }
    }
    return DayOption;
  }, [rrule]);

  const monthIntervalOptions = useMemo(() => {
    // if the selected frequency is "Day", show up to 31 values in the interval.
    // if the selected frequency is a weekday (mon -> sun), show 5 values in the interval.
    const entries = currentFrequency === DayOption ? 31 : 5;
    return Array.from([...Array(entries)], (value: any, i: number) => {
      const text = ordinalSuffixOf(i + 1);
      if (!value) {
        return {
          key: i + 1,
          value: i + 1,
          text,
        };
      }
      return {
        key: value,
        value,
        text,
      };
    });
  }, [currentFrequency]);

  const monthFrequencyOptions = useMemo(
    () => [
      {
        value: DayOption,
        text: capitalize(DayOption),
      },
      ...weekDays.map((_, index: number) => ({
        value: index.toString(),
        text: dayOfWeek[index],
      })),
    ],
    [],
  );

  const onChange = (value: any, type?: MonthOptionTypeEnum) => {
    if (type === MonthOptionTypeEnum.Interval) {
      if (currentFrequency === DayOption) {
        // if the interval changes, and the selected frequency is "Day"
        // utilize the bymonthday value
        onSelectMonthModifier({ bymonthday: value, byweekday: null });
      } else {
        // otherwise, if the frequency is a weekday
        // use the byweekday value
        const correctDay = weekDays[currentFrequency];
        onSelectMonthModifier({
          byweekday: correctDay?.nth(value),
          bymonthday: null,
        });
      }
    } else if (type === MonthOptionTypeEnum.Frequency) {
      if (value === DayOption) {
        onSelectMonthModifier({
          bymonthday: currentInterval,
          byweekday: null,
        });
      } else {
        // if we switch the frequency to a weekday,
        // and the interval selected is greater than 5,
        // set it to the 1st interval
        // (since there can't be more than 5 intervals of a weekday in a month)
        const interval = currentInterval > 5 ? 1 : currentInterval;
        const correctDay = weekDays[value];
        onSelectMonthModifier({
          byweekday: correctDay?.nth(interval),
          bymonthday: null,
        });
      }
    }
  };

  return (
    <Container>
      <SingleSelectMenu
        options={monthIntervalOptions}
        value={currentInterval}
        onChange={(value) => onChange(value, MonthOptionTypeEnum.Interval)}
        customWidth="60px"
      />
      <SingleSelectMenu
        options={monthFrequencyOptions}
        value={currentFrequency.toString()}
        onChange={(value) => onChange(value, MonthOptionTypeEnum.Frequency)}
        customWidth="60px"
      />
    </Container>
  );
});
